import { LogoutModal } from "@app/components/modals/AuthModal";
import { Link } from "@app/router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
  useModal,
} from "@shortsworks/ui";
import { AssetImporter } from "@app/components/AssetImporter";
import { LogOut } from "lucide-react";
import { useEffect, useState } from "react";
import { MainMenu, MenuItem } from "@app/components/layouts/LNB/menus";
import { useLocation, useNavigate, useRoutes } from "react-router-dom";
import { Path } from "@app/route_path";
import { clearAuthToken } from "@shortsworks/user-api";
import { DEVICE, USER_DEVICE_TYPE } from "@app/types/device";
import { flutterCommunicationCode, sendToFlutter } from "@app/types/flutter";

const LogoutButton = () => {
  const navigate = useNavigate();
  const { showModal } = useModal();

  const handleLogout = async () => {
    const logoutModalResult = await showModal(LogoutModal).promise();

    if (logoutModalResult.value) {
      clearAuthToken();
      const userDeviceType = sessionStorage.getItem(USER_DEVICE_TYPE);
      if (userDeviceType === DEVICE.MOBILE) {
        return sendToFlutter(flutterCommunicationCode.LOGOUT);
      }
      return navigate(Path.LOGIN);
    }
  };

  return (
    <SidebarMenuSubButton className="cursor-pointer" onClick={handleLogout}>
      <LogOut className="h-4 w-4" />
      <div>로그아웃</div>
    </SidebarMenuSubButton>
  );
};

export const LNB = () => {
  const {
    state,
    open,
    setOpen,
    openMobile,
    setOpenMobile,
    isMobile,
    toggleSidebar,
  } = useSidebar();

  const [mainMenu, setMainMenu] = useState<MenuItem[]>(MainMenu);
  const location = useLocation();
  const push = useNavigate();

  useEffect(() => {
    const determineIsParentActive = (menu: MenuItem, path: string) => {
      return !open && menu.path === path && menu.items?.length === 0;
    };

    const updatedMenu = mainMenu.map((menu) => {
      const updatedItems = (menu.items || []).map((item) => ({
        ...item,
        isActive: item.path === location.pathname,
      }));

      const isParentActive =
        (!open && updatedItems.some((item) => item.isActive)) ||
        determineIsParentActive(menu, location.pathname);

      return {
        ...menu,
        isActive: isParentActive,
        items: updatedItems,
      };
    });

    setMainMenu(updatedMenu);
  }, [location, open]);

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader className="h-[60px] flex items-center justify-center">
        {open && <AssetImporter name="logoH" className="h-3.5" />}
        {!open && <AssetImporter name="logoSymbol" className="h-3.5" />}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {mainMenu.map((menu, index) => {
              if (open) {
                return getDefaultMenu(menu, (path) => {
                  push(path);
                });
              }

              if (!menu.items?.length) {
                return getDefaultMenu(menu, (path) => {
                  push(path);
                });
              }

              return getIconItem(menu, (path) => {
                push(path);
              });
            })}
            <LogoutButton />
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter></SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

const getDefaultMenu = (menu: MenuItem, onClick: (path: Path) => void) => {
  return (
    <SidebarMenuItem key={menu.title}>
      <SidebarMenuButton
        tooltip={menu.title}
        isActive={menu.isActive}
        onClick={() => {
          onClick(menu.path);
        }}
      >
        {menu.icon && <menu.icon className="h-4 w-4" />}
        <span>{menu.title}</span>
      </SidebarMenuButton>
      {menu.items?.length ? (
        <SidebarMenuSub>
          {menu.items.map((item) => (
            <SidebarMenuSubItem key={item.title}>
              <SidebarMenuSubButton
                isActive={item.isActive}
                className="cursor-pointer"
                onClick={() => {
                  onClick(item.path);
                }}
              >
                {item.icon && <item.icon className="h-4 w-4" />}
                {item.title}
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  );
};

const getIconItem = (menu: MenuItem, onClick: (path: Path) => void) => {
  if (menu.items) {
    return (
      <SidebarMenuItem key={menu.title}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuItem key={menu.title}>
              <SidebarMenuButton isActive={menu.isActive} tooltip={menu.title}>
                {menu.icon && <menu.icon className="h-4 w-4" />}
                <span>{menu.title}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side="right"
            align="start"
            sideOffset={4}
          >
            {menu.items.map((item) => (
              <DropdownMenuItem
                className="gap-2 p-2"
                key={item.title}
                onClick={() => {
                  onClick(item.path);
                }}
              >
                {item.icon && <item.icon className="h-4 w-4" />}
                <span>{item.title}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }
};
